import { getFirstLettersFromText } from '@packages/toolbox/dist/utils/string';
import { generateFIO, generateFIOInitials } from '@packages/toolbox/dist/utils/user';
import { keyBy } from 'lodash';
import { defineStore } from 'pinia';

import { ROLE_OPERATOR, ROLE_SUPERADMINISTRATOR, ROLE_USER } from 'SharedDir/constants/role';
import { UserInfo } from 'SharedDir/declarations/access';
import { ProfileType } from 'SharedDir/declarations/profile-types';
import { WithRelations } from 'SharedDir/declarations/shared';
import { UserProfile, UserProfileBare } from 'SharedDir/declarations/user-profiles';
import { cookiesStorage } from 'SharedDir/stores/cookiesStorage';

export interface UserStateInterface {
  currentProfile: null | WithRelations<UserProfileBare>;
  user: null | UserInfo;
  userProfiles: Array<UserProfileBare>;
  userProfileTypes: Array<ProfileType>;
}

function getPersistOptions() {
  if (process.env.SERVER) return false;

  return [{ storage: localStorage }, { storage: cookiesStorage, paths: ['currentProfile.id'] }];
}

export const useUserStore = defineStore('user', {
  persist: getPersistOptions(),

  state: (): UserStateInterface => ({
    user: null,
    currentProfile: null,
    userProfiles: [],
    userProfileTypes: [],
  }),

  getters: {
    getUser: (state) => state.user,

    /**
     * Получить первые буквы Фамилии и Имя
     * Необходимо чаще всего для вставки в аватарки
     *
     * @param state
     * @returns
     */
    firstLetters: (state): string => {
      if (state?.user) {
        return getFirstLettersFromText(`${state.user.lastName} ${state.user.firstName}`);
      }
      return '';
    },

    /**
     *  Получить полное Фамилия и Имя
     *
     * @param state
     * @returns
     */
    fullName: (state): string => {
      if (state?.user) {
        return `${state.user.lastName} ${state.user.firstName}`;
      }
      return '';
    },

    /**
     * Получить Фамилию И.О.
     * @param state
     * @returns
     */
    fullLastNameWithInitials: (state): string => {
      if (state?.user) {
        return generateFIOInitials(
          state.user.lastName,
          state.user.firstName,
          state.user.middleName
        );
      }
      return '';
    },

    /**
     *  Получить полное Фамилия Имя Отчество
     *
     * @param state
     * @returns
     */
    fio: (state): string => {
      if (state?.user) {
        return generateFIO(state.user.lastName, state.user.firstName, state.user.middleName);
      }
      return '';
    },

    /**
     * Пользователь Суперадмин
     *
     * @param state
     * @returns
     */
    isRoot: (state): boolean => {
      if (state?.user) {
        return state.user.roleId === ROLE_SUPERADMINISTRATOR.id;
      }
      return false;
    },

    /**
     * Пользователь Просто пользователь
     *
     * @param state
     * @returns
     */
    isSimpleUser: (state): boolean => {
      if (state?.user) {
        return state.user.roleId === ROLE_USER.id;
      }
      return false;
    },

    /**
     *  Получить наименование роли пользователя
     *
     * @param state
     * @returns
     */
    roleName: (state): string => {
      if (state?.user) {
        return state?.user?.role?.name ?? '';
      }
      return '';
    },
  },

  actions: {
    setUser(userData: UserInfo | null) {
      this.user = userData;
    },
    clearUser() {
      this.user = null;
    },
    updateUser(userData: UserInfo) {
      Object.keys(userData).forEach((key) => {
        if ((userData[key] !== null || userData[key] !== 'undefined') && this.user) {
          this.user[key] = userData[key];
        }
      });
    },
    /**
     *
     * @param {Array<UserProfile>} profiles
     * @param {() => (void | Promise<void>)} noCurrentProfileFallback
     * @returns {Promise<void>}
     */
    async setUserProfiles({
      profiles,
      noCurrentProfileFallback,
    }: {
      profiles: UserProfileBare[];
      noCurrentProfileFallback?: () => void | Promise<void>;
    }) {
      this.userProfiles = profiles;
      const existingProfiles = keyBy(profiles, 'id');
      const existingProfilesId = Object.keys(existingProfiles).map((key) => +key);
      if (this.currentProfile && !existingProfilesId.includes(this.currentProfile.id)) {
        this.changeProfile(null);
        if (noCurrentProfileFallback) {
          await noCurrentProfileFallback();
        }
      } else if (this.currentProfile) {
        this.changeProfile(existingProfiles[this.currentProfile.id]);
      }
    },
    addUserProfile(profile) {
      this.userProfiles.push(profile);
    },
    setUserProfileTypes(values) {
      this.userProfileTypes = values;
    },
    changeProfile(profile) {
      this.currentProfile = profile;
    },
  },
});
